import { useState, useEffect, useRef } from 'react'
import { useNCMCache } from '../hooks/useNCMCache'

export const NCMSearchInput = ({ 
  value, 
  onChange, 
  onBlur,
  error,
  placeholder = "Digite código ou descrição do NCM",
  disabled = false,
  setValue,
  onRequestCreateNCM = null,
  onClearNCMCode = null
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [isDisabled, setIsDisabled] = useState(false)
  
  const inputRef = useRef(null)
  const dropdownRef = useRef(null)
  
  const {
    searchTerm,
    setSearchTerm,
    filteredNCMs,
    isLoading,
    error: ncmError,
    formatNCMOption,
    findNCMByCode,
    isValidNCMCode,
    hasMinSearchLength
  } = useNCMCache()

  // Sincronizar valor externo com input interno
  useEffect(() => {
    if (value) {
      const ncm = findNCMByCode(value)
      if (ncm) {
        const formattedValue = formatNCMOption(ncm)
        if (inputValue !== formattedValue) {
          setInputValue(formattedValue)
        }
      }
    } else if (value === '' && inputValue !== '') {
      setInputValue('')
    }
  }, [value, findNCMByCode, formatNCMOption, inputValue])

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
        setSelectedIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleInputChange = (e) => {
    const newValue = e.target.value
    setInputValue(newValue)
    setSearchTerm(newValue)
    setIsOpen(true)
    setSelectedIndex(-1)
    onChange(newValue)
  }

  const handleInputFocus = () => {
    if (inputValue.length >= 2) {
      setSearchTerm(inputValue)
      setIsOpen(true)
    }
  }

  const handleInputBlur = () => {
    setTimeout(() => {
      if (onBlur) {
        const isValid = isValidNCMCode(value)
        onBlur(isValid)
      }
    }, 200)
  }

  const selectNCM = (ncm) => {
    const formattedValue = formatNCMOption(ncm)
    setInputValue(formattedValue)
    onChange(ncm.code)
    setIsOpen(false)
    setSelectedIndex(-1)
    setSearchTerm('')
    setValue('ncm_code', ncm.code)
    setValue('ncm_description', ncm.description || '')
    setIsDisabled(true)
  }

  const handleKeyDown = (e) => {
    if (!isOpen || filteredNCMs.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => 
          prev < filteredNCMs.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : filteredNCMs.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && selectedIndex < filteredNCMs.length) {
          selectNCM(filteredNCMs[selectedIndex])
        }
        break
      case 'Escape':
        setIsOpen(false)
        setSelectedIndex(-1)
        inputRef.current?.blur()
        break
      default:
        break
    }
  }

  const handleCreateNewNCM = () => {
    setIsOpen(false)
    if (onRequestCreateNCM) {
      onRequestCreateNCM(searchTerm)
    }
  }

  const handleClearValue = () => {
    setInputValue('')
    onChange('')
    setSearchTerm('')
    setIsOpen(false)
    setSelectedIndex(-1)
    setIsDisabled(false)
    if (onClearNCMCode) {
      onClearNCMCode()
    }
    inputRef.current?.focus()
  }

  const shouldShowDropdown = isOpen && hasMinSearchLength && !isLoading
  const hasResults = filteredNCMs.length > 0
  const showNoResults = shouldShowDropdown && !hasResults && !ncmError
  const hasValue = value && value.trim() !== ''

  return (
    <div className="relative" ref={dropdownRef}>
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={isDisabled}
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
          error ? 'border-red-300' : 'border-gray-300'
        } ${disabled ? 'bg-gray-50 cursor-not-allowed' : ''} ${hasValue ? 'pr-10' : ''}`}
      />

      {/* Clear button - só mostra quando há valor */}
      {hasValue && !disabled && (
        <button
          type="button"
          onClick={handleClearValue}
          className="absolute right-3 top-2.5 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100 z-10"
          title="Limpar campo"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}

      {/* Loading indicator */}
      {isLoading && hasMinSearchLength && !hasValue && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
        </div>
      )}

      {/* Dropdown */}
      {shouldShowDropdown && (
        <div className="absolute z-[9999] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-[144px] overflow-y-auto">
          {hasResults ? (
            <>
              {filteredNCMs.map((ncm, index) => (
                <div
                  key={ncm.code}
                  className={`px-3 py-2 cursor-pointer text-sm ${
                    index === selectedIndex
                      ? 'bg-blue-500 text-white'
                      : 'hover:bg-gray-100'
                  }`}
                  onClick={() => selectNCM(ncm)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="font-medium">{ncm.code}</div>
                  {ncm.description && (
                    <div className={`text-xs ${
                      index === selectedIndex ? 'text-blue-100' : 'text-gray-500'
                    }`}>
                      {ncm.description}
                    </div>
                  )}
                </div>
              ))}
            </>
          ) : showNoResults ? (
            <div className="px-3 py-2">
              <div className="text-sm text-gray-500 mb-2">
                Nenhum NCM encontrado para "{searchTerm}"
              </div>
              <button
                type="button"
                onClick={handleCreateNewNCM}
                className="w-full px-3 py-2 text-sm bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-md transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Cadastrar novo NCM
              </button>
            </div>
          ) : null}

          {ncmError && (
            <div className="px-3 py-2 text-sm text-red-600">
              Erro ao carregar NCMs. Tente novamente.
            </div>
          )}
        </div>
      )}

      {/* Helper text */}
      <p className="mt-1 mb-0 text-xs text-gray-500">
        {!hasMinSearchLength && inputValue.length > 0 && inputValue.length < 2
          ? 'Digite pelo menos 2 caracteres para pesquisar'
          : hasMinSearchLength && !isLoading && filteredNCMs.length > 0
          ? `${filteredNCMs.length} resultado(s) encontrado(s)`
          : 'Digite código ou descrição do NCM'
        }
      </p>
    </div>
  )
} 